import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Download, Info, CheckCircle2, CheckCircle, XCircle, TrendingDown, Calendar, FileText,
  ClipboardList, ShieldAlert, Flame, Lock, Unlock, Trash2, ArrowLeft, Share2, MessageSquare,
  AlertCircle, BarChart3,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import ShareModal from '../components/ShareModal';
import ValideClientBadge from '../components/ValideClientBadge';
import ShareAccessBadge from '../components/ShareAccessBadge';
import RapportAuditHistory from '../components/RapportAuditHistory';
import PdfPreviewModal from '../components/PdfPreviewModal';
import EligibilityExplainer from '../components/EligibilityExplainer';
import { downloadPdf } from '../utils/downloadPdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// === Libellés des motifs (cohérent avec le reste de l'app) ===
const MOTIF_LABELS = {
  EXONERATION: 'Exonération',
  EXO_USAGE_NON_COMBUSTIBLE: 'Exo. — Usage non combustible',
  EXO_DOUBLE_USAGE: 'Exo. — Double usage',
  EXO_MINERAL_NON_METALLIQUE: 'Exo. — Minéraux non métalliques',
  EXO_PROD_PRODUITS_ENERGETIQUES: 'Exo. — Production de produits énergétiques',
  EXO_PROD_ELECTRICITE: 'Exo. — Production d\'électricité',
  EXO_BIOGAZ_NON_INJECTE: 'Exo. — Biogaz non injecté',
  EXO_NAVIGATION_INTERIEURE: 'Exo. — Navigation intérieure',
  EXO_NAVIGATION_MARITIME: 'Exo. — Navigation maritime',
  EXO_NAVIGATION_AERIENNE: 'Exo. — Navigation aérienne',
  QUOTAS_CO2: 'Tarif réduit — Quotas CO2',
  CONCURRENCE_INTERNATIONALE: 'Tarif réduit — Concurrence internationale',
  DESHYDRATATION_LEGUMES: 'Tarif réduit — Déshydratation légumes',
  CARBURANT: 'Tarif réduit — Carburant',
  TRAVAUX_AGRICOLES: 'Tarif réduit — Travaux agricoles',
  RATIO_VALEUR_PRODUCTION: 'Tarif réduit — Ratio Valeur de Production',
  NORMAL: 'Normal',
};

const RapportTICGN = ({ readOnly = false, shareData = null, shareToken = null }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth?.() || {};
  const userRole = (auth?.user?.role || '').toLowerCase();
  const isAdmin = userRole === 'admin' || userRole === 'energy_manager';
  const [rapport, setRapport] = useState(shareData);
  const [loading, setLoading] = useState(!shareData);
  const [activeTab, setActiveTab] = useState('overview');
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);

  const pdfUrl = (readOnly && shareToken)
    ? `${API_URL}/api/public/share/${shareToken}/pdf`
    : (id ? `${API_URL}/api/ticgn/rapports/${id}/pdf` : null);
  const pdfFilename = `rapport_ticgn_${rapport?.siren || rapport?.nom_entreprise || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;

  useEffect(() => {
    if (shareData) {
      setRapport(shareData);
      setLoading(false);
      return;
    }
    fetchRapport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, shareData]);

  const fetchRapport = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ticgn/rapports/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setRapport(await res.json());
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce rapport TICGN ?')) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/ticgn/rapports/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    navigate('/dashboard?tab=ticgn');
  };

  const handleLock = async () => {
    const token = localStorage.getItem('token');
    const action = rapport.locked ? 'unlock' : 'lock';
    await fetch(`${API_URL}/api/ticgn/rapports/${id}/${action}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
    fetchRapport();
  };

  const downloadPDF = async () => {
    const url = (readOnly && shareToken)
      ? `/api/public/share/${shareToken}/pdf`
      : `/api/ticgn/rapports/${id}/pdf`;
    const filename = `rapport_ticgn_${rapport?.siren || rapport?.nom_entreprise || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;
    try {
      await downloadPdf({ url, filename, includeAuth: !(readOnly && shareToken) });
    } catch { /* toast géré par l'utilitaire */ }
  };


  const formatCurrency = (v) => (v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  const formatNumber = (v, d = 2) => (v || 0).toLocaleString('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d });
  const formatPercent = (v, d = 3) => ((v || 0) * 100).toFixed(d).replace('.', ',') + ' %';
  const formatDateFR = (date) => {
    const mois = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    return `${mois[date.getMonth()]} ${date.getFullYear()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F97316]" />
      </div>
    );
  }

  if (!rapport) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Rapport non trouvé</h1>
          <button onClick={() => navigate('/dashboard?tab=ticgn')} className="mt-2 text-[#F97316]">Retour au tableau de bord</button>
        </div>
      </div>
    );
  }

  // === Données dérivées ===
  const pces = rapport.pces_data || [];
  const isEligible = rapport.eligibilite_globale === 'ELIGIBLE';
  const economies_att = rapport.economies_attestation_total || 0;
  const economies_remb = rapport.economies_remboursement || {};
  const total_remb = Object.values(economies_remb).reduce((s, v) => s + v, 0);

  // Période de couverture de l'attestation TICGN (12 mois glissants à compter du dépôt)
  // L'attestation prend effet à la date de dépôt et couvre 12 mois.
  const dateAujourdhui = new Date();
  const dateAttestationFin = new Date(dateAujourdhui);
  dateAttestationFin.setFullYear(dateAttestationFin.getFullYear() + 1);
  const anneeAttDebut = dateAujourdhui.getFullYear();
  const anneeAttFin = dateAttestationFin.getFullYear();
  const libellePeriodeAttestation = anneeAttDebut === anneeAttFin
    ? `${anneeAttDebut}`
    : `${anneeAttDebut} → ${anneeAttFin}`;
  const libellePeriodeRemboursement = Object.keys(economies_remb).sort().join(', ') || '—';
  const totalConsoMWh = rapport.total_conso_mwh || pces.reduce((s, p) => s + (p.total_conso_mwh || 0), 0);
  const totalTicgnCerfaOfficiel = rapport.total_ticgn_cerfa_officiel || pces.reduce((s, p) => s + (p.ticgn_cerfa_officiel || 0), 0);
  const totalTicgnReel = rapport.total_ticgn_cerfa || pces.reduce((s, p) => s + (p.total_ticgn_cerfa || 0), 0);
  const ratioCerfaOk = (rapport.ratio_global || 0) >= 0.005;

  // VP / Quotas / VP par SIRET
  const vpData = rapport.valeur_production || null;
  const vpEligible = !!rapport.valeur_production_eligible;
  const vpCoutTotal = vpData ? (vpData.cout_total_fiscal ?? vpData.factures_total_htva ?? 0) : 0;
  const vpLignes = vpData?.lignes_energies || [];
  const vpMethodeNominale = vpLignes.length > 0;
  const soumisQuotasCO2 = !!rapport.soumis_quotas_co2 ||
    (rapport.sites || []).some(s => s.soumis_quotas_co2);
  const vpParSiret = rapport.vp_par_siret || {};
  const vpParSiretEntries = Object.entries(vpParSiret);
  const vpParSiretEligibles = vpParSiretEntries.filter(([, v]) => v.eligible_vp);

  // === Calcul des VOIES d'éligibilité (à partir des données du rapport) ===
  const motifsValidants = new Set();
  pces.forEach(p => {
    (p.tranches_detail || []).forEach(t => {
      if (t.eligible && t.motif && t.motif !== 'NORMAL') motifsValidants.add(t.motif);
    });
  });

  // Critères "renseignés" (utilisés pour décider d'afficher la voie en non-éligible)
  const vpRenseignee = vpData && (vpCoutTotal > 0 || (vpData.valeur_production_calculee || 0) > 0);
  const vpSiretRenseignee = vpParSiretEntries.length > 0;
  const quotasRenseignee = soumisQuotasCO2;
  const duRenseignee = pces.some(p => (p.tranches_detail || []).some(t => t.motif === 'EXO_DOUBLE_USAGE'));
  const icpeRenseignee = pces.some(p => p.icpe_rubrique?.numero);

  const voies = [];
  // A — Ratio CERFA TICGN/VAC ≥ 0,5 %
  const voieA_eligible = ratioCerfaOk && motifsValidants.size > 0 &&
    [...motifsValidants].some(m => !m.startsWith('EXO_') && m !== 'EXONERATION' && m !== 'RATIO_VALEUR_PRODUCTION');
  voies.push({
    key: 'A',
    label: 'A',
    titre: 'Ratio CERFA TICGN/VAC ≥ 0,5 %',
    valeur: `Ratio calculé : ${formatPercent(rapport.ratio_global)} (TICGN CERFA ${formatCurrency(totalTicgnCerfaOfficiel)} / VAC ${formatCurrency(rapport.valeur_ajoutee)})`,
    detail: 'Ouvre les tarifs réduits classiques (Quotas CO2, Concurrence internationale, Déshydratation…).',
    statut: voieA_eligible ? 'eligible' : (ratioCerfaOk ? 'rate' : 'rate_ratio'),
    renseignee: true,
  });

  // A bis — Exonération NAF
  const voieAbis_eligible = motifsValidants.has('EXONERATION') ||
    [...motifsValidants].some(m => m.startsWith('EXO_'));
  voies.push({
    key: 'A_exo',
    label: 'A bis',
    titre: 'Exonération NAF / activité (sans condition de ratio)',
    valeur: voieAbis_eligible
      ? `${pces.filter(p => (p.tranches_detail || []).some(t => t.eligible && (t.motif === 'EXONERATION' || (t.motif || '').startsWith('EXO_')))).length} PCE(s) sous exonération`
      : 'Aucun PCE sous régime d\'exonération',
    detail: 'Codes NAF d\'office exonérés ou PCE déclarant un usage non combustible / double usage / minéraux non métalliques, etc.',
    statut: voieAbis_eligible ? 'eligible' : 'na',
    renseignee: voieAbis_eligible,
  });

  // B — Ratio Valeur de Production > 3 %
  voies.push({
    key: 'B',
    label: 'B',
    titre: 'Ratio Valeur de Production > 3 %',
    valeur: vpRenseignee
      ? `Ratio calculé : ${((vpData.ratio_vp || 0) * 100).toFixed(2).replace('.', ',')} %  (${vpMethodeNominale ? 'Σ Conso × Tarif' : 'Σ Factures HTVA'} ${formatCurrency(vpCoutTotal)} / VP ${formatCurrency(vpData.valeur_production_calculee || 0)})`
      : 'Non renseigné (étape 2 du formulaire)',
    detail: 'Tarif réduit RATIO_VALEUR_PRODUCTION — entreprise dite "énergo-intensive" (art. 265 nonies CdD).',
    statut: vpEligible ? 'eligible' : (vpRenseignee ? 'rate' : 'na'),
    renseignee: vpRenseignee,
  });

  // B bis — VP par SIRET (multi-sites uniquement, si renseigné)
  if (vpSiretRenseignee) {
    voies.push({
      key: 'Bbis',
      label: 'B bis',
      titre: 'VP par SIRET (fallback multi-sites)',
      valeur: `${vpParSiretEligibles.length}/${vpParSiretEntries.length} SIRET(s) au-dessus de 3 %`,
      detail: vpParSiretEntries.map(([siret, v]) =>
        `${siret} : ${((v.ratio_vp || 0) * 100).toFixed(2).replace('.', ',')} % ${v.eligible_vp ? '✓' : '✗'}`
      ).join(' • '),
      statut: vpParSiretEligibles.length > 0 ? 'eligible' : 'rate',
      renseignee: true,
    });
  }

  // C — Quotas CO2 (SEQE-UE)
  const voieC_eligible = motifsValidants.has('QUOTAS_CO2');
  voies.push({
    key: 'C',
    label: 'C',
    titre: 'Soumis aux Quotas CO2 (SEQE-UE)',
    valeur: quotasRenseignee
      ? (voieC_eligible ? 'Coché — ratio CERFA validant ≥ 0,5 %' : 'Coché mais ratio CERFA insuffisant')
      : 'Non coché (étape 2 du formulaire)',
    detail: 'Ouvre le motif tarif réduit QUOTAS_CO2 (toujours soumis au ratio TICGN/VAC ≥ 0,5 %).',
    statut: voieC_eligible ? 'eligible' : (quotasRenseignee ? 'rate' : 'na'),
    renseignee: quotasRenseignee,
  });

  // D — Double usage
  const voieD_eligible = motifsValidants.has('EXO_DOUBLE_USAGE');
  voies.push({
    key: 'D',
    label: 'D',
    titre: 'Double usage (exonération matières premières)',
    valeur: duRenseignee
      ? (() => {
          const items = [];
          pces.forEach(p => (p.tranches_detail || []).forEach(t => {
            if (t.motif === 'EXO_DOUBLE_USAGE') {
              items.push(`${p.pce}${t.cas_code ? ' — ' + t.cas_code : ''}`);
            }
          }));
          return items.join(' • ');
        })()
      : 'Non coché',
    detail: 'Cas réglementaires (art. 265 nonies CdD) : réduction chimique, électrolyse, métallurgie, génération de substance.',
    statut: voieD_eligible ? 'eligible' : (duRenseignee ? 'rate' : 'na'),
    renseignee: duRenseignee,
  });

  // E — Rubrique ICPE qualifiante
  const voieE_eligible = pces.some(p => p.icpe_motif);
  voies.push({
    key: 'E',
    label: 'E',
    titre: 'Rubrique ICPE qualifiante',
    valeur: icpeRenseignee
      ? pces.filter(p => p.icpe_rubrique?.numero).map(p => `${p.pce} — Rubrique ${p.icpe_rubrique.numero}`).join(' • ')
      : 'Aucune rubrique ICPE renseignée',
    detail: 'Produits minéraux non métalliques (rubriques 2510–2531) → exonération EXO_MINERAL_NON_METALLIQUE.',
    statut: voieE_eligible ? 'eligible' : (icpeRenseignee ? 'rate' : 'na'),
    renseignee: icpeRenseignee,
  });

  // Filtrage de l'onglet Éligibilité — règles métier :
  //   • ELIGIBLE → garder uniquement les voies validantes (statut = 'eligible')
  //   • NON ÉLIGIBLE → garder uniquement les voies "renseignées" (la voie A est toujours
  //     considérée renseignée puisqu'on a forcément un ratio CERFA et un NAF)
  const voiesAffichees = isEligible
    ? voies.filter(v => v.statut === 'eligible')
    : voies.filter(v => v.renseignee);

  const voiesValidantes = voies.filter(v => v.statut === 'eligible');

  // === Charts ===
  // Plafond CERFA TICGN : on ne peut plus récupérer au-delà de N-2
  const MIN_ANNEE_RECUPERABLE = new Date().getFullYear() - 2;

  const consoChartData = [];
  pces.forEach(pce => (pce.detail_mensuel || []).forEach(d => {
    const existing = consoChartData.find(c => c.mois === d.mois);
    if (existing) existing.conso += d.conso_mwh; else consoChartData.push({ mois: d.mois, conso: d.conso_mwh });
  }));

  const economiesParAnnee = Object.entries(economies_remb)
    .map(([annee, montant]) => ({ annee, montant, estimation: false }))
    .filter(e => e.montant > 0 && parseInt(e.annee, 10) >= MIN_ANNEE_RECUPERABLE)
    .sort((a, b) => a.annee.localeCompare(b.annee));

  // Estimations N-1 et N+1 hachurées (potentiel à confirmer).
  // Le graphique et le KPI Total INCLUENT désormais ces estimations pour rester cohérents.
  const economiesParAnneeAvecEstimations = (() => {
    if (economiesParAnnee.length === 0) return [];
    const avg = economiesParAnnee.reduce((s, e) => s + e.montant, 0) / economiesParAnnee.length;
    const minYear = parseInt(economiesParAnnee[0].annee, 10);
    const maxYear = parseInt(economiesParAnnee[economiesParAnnee.length - 1].annee, 10);
    const series = [...economiesParAnnee];
    if (minYear - 1 >= MIN_ANNEE_RECUPERABLE) {
      series.unshift({ annee: String(minYear - 1), montant: avg, estimation: true });
    }
    series.push({ annee: String(maxYear + 1), montant: avg, estimation: true });
    return series;
  })();

  // Décomposition explicite pour le KPI Total :
  //   total_potentiel = attestation + remb_reel + remb_estime
  const total_remb_estime = economiesParAnneeAvecEstimations
    .filter(e => e.estimation)
    .reduce((s, e) => s + e.montant, 0);
  const total_potentiel = economies_att + total_remb + total_remb_estime;

  // Données du graphique global : remboursement (réel + estimé) + attestation à la fin
  const graphiqueGlobal = [
    ...economiesParAnneeAvecEstimations.map(e => ({
      label: e.annee,
      montant: e.montant,
      type: e.estimation ? 'estime' : 'reel',
      typeLabel: e.estimation ? 'Remboursement estimé' : 'Remboursement réel',
    })),
    ...(economies_att > 0 ? [{
      label: libellePeriodeAttestation,
      montant: economies_att,
      type: 'attestation',
      typeLabel: 'Attestation (économie future)',
    }] : []),
  ];

  // === Recommandation périodes (inchangé) ===
  const calculerRecommandationPeriode = () => {
    if (!rapport.fin_periode || !rapport.debut_periode || !isEligible) return null;
    const debutPeriodeAnalysee = new Date(rapport.debut_periode);
    const finPeriodeAnalysee = new Date(rapport.fin_periode);
    const aujourdHui = new Date();
    const moisAnalyses = Math.round((finPeriodeAnalysee - debutPeriodeAnalysee) / (30 * 24 * 60 * 60 * 1000));
    const economiesAnalysees = economies_att + total_remb;
    const economiesMensuellesRemboursement = moisAnalyses > 0 ? total_remb / moisAnalyses : 0;
    const economiesMensuelles = moisAnalyses > 0 ? economiesAnalysees / moisAnalyses : 0;
    const debutProchains12Mois = new Date(aujourdHui.getFullYear(), aujourdHui.getMonth() + 1, 1);
    const finProchains12Mois = new Date(debutProchains12Mois); finProchains12Mois.setFullYear(finProchains12Mois.getFullYear() + 1); finProchains12Mois.setDate(finProchains12Mois.getDate() - 1);
    const debutPeriodeManquante = new Date(finPeriodeAnalysee); debutPeriodeManquante.setDate(debutPeriodeManquante.getDate() + 1);
    const finPeriodeManquante = new Date(debutProchains12Mois); finPeriodeManquante.setDate(finPeriodeManquante.getDate() - 1);
    const moisManquants = Math.max(0, Math.round((finPeriodeManquante - debutPeriodeManquante) / (30 * 24 * 60 * 60 * 1000)));
    const dateMinRecuperableTicgn = new Date(aujourdHui.getFullYear() - 2, 0, 1);
    const finPeriodePrecedente = new Date(debutPeriodeAnalysee); finPeriodePrecedente.setDate(finPeriodePrecedente.getDate() - 1);
    const debutPeriodePrecedenteSouhaite = new Date(finPeriodePrecedente); debutPeriodePrecedenteSouhaite.setFullYear(debutPeriodePrecedenteSouhaite.getFullYear() - 1); debutPeriodePrecedenteSouhaite.setDate(debutPeriodePrecedenteSouhaite.getDate() + 1);
    const debutPeriodePrecedente = debutPeriodePrecedenteSouhaite < dateMinRecuperableTicgn ? new Date(dateMinRecuperableTicgn) : debutPeriodePrecedenteSouhaite;
    const moisPeriodePrecedente = Math.max(0, (finPeriodePrecedente.getFullYear() - debutPeriodePrecedente.getFullYear()) * 12 + (finPeriodePrecedente.getMonth() - debutPeriodePrecedente.getMonth()) + 1);
    const periodePrecedenteRecuperable = moisPeriodePrecedente >= 1 && finPeriodePrecedente >= dateMinRecuperableTicgn;
    const economiesEstimees = economiesMensuellesRemboursement * moisManquants;
    const economiesEstimeesPrecedente = economiesMensuellesRemboursement * moisPeriodePrecedente;
    const result = {
      moisAnalyses, economiesAnalysees, economiesMensuelles, economiesRemboursement: total_remb, economiesMensuellesRemboursement,
      hasPeriodeFuture: moisManquants >= 1, debutProchains12Mois, finProchains12Mois, debutPeriodeManquante, finPeriodeManquante, moisManquants, economiesEstimees,
      hasPeriodePrecedente: periodePrecedenteRecuperable, debutPeriodePrecedente, finPeriodePrecedente, moisPeriodePrecedente, economiesEstimeesPrecedente, dateMinRecuperableTicgn,
    };
    if (result.hasPeriodeFuture || result.hasPeriodePrecedente) return result;
    return null;
  };
  const recommandation = calculerRecommandationPeriode();

  // === Estimations années précédentes (cartes pointillées) ===
  // Réintroduites pour offrir une vue complète du potentiel (cohérent avec le graphique + KPI total).
  const realYears = Object.entries(economies_remb).map(([y, v]) => ({ year: y, montant: v, real: true })).filter(e => e.montant > 0);
  const realYearsSet = new Set(realYears.map(e => parseInt(e.year, 10)));
  let mensuelle = 0;
  if (rapport.debut_periode && rapport.fin_periode && total_remb > 0) {
    const debut = new Date(rapport.debut_periode);
    const fin = new Date(rapport.fin_periode);
    const moisAn = Math.max(1, (fin.getFullYear() - debut.getFullYear()) * 12 + (fin.getMonth() - debut.getMonth()) + 1);
    mensuelle = total_remb / moisAn;
  }
  const aujourdHuiCards = new Date();
  const minYearCards = aujourdHuiCards.getFullYear() - 2;
  const candidates = [aujourdHuiCards.getFullYear() - 1, aujourdHuiCards.getFullYear() - 2];
  const estimatedYears = candidates
    .filter(y => y >= minYearCards && !realYearsSet.has(y) && mensuelle > 0)
    .map(y => ({ year: String(y), montant: mensuelle * 12, real: false }));
  const allYears = [...realYears, ...estimatedYears].sort((a, b) => a.year.localeCompare(b.year));

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble' },
    { id: 'analysis', label: 'Analyse financière' },
    { id: 'details', label: 'Détails PCE' },
    { id: 'eligibility', label: 'Éligibilité' },
    { id: 'informations', label: 'Informations' },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="rapport-ticgn">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!readOnly && (
              <button onClick={() => navigate('/dashboard?tab=ticgn')} className="p-2 hover:bg-[#F5F6F9] rounded-lg" data-testid="rapport-back-btn">
                <ArrowLeft className="w-5 h-5 text-[#687175]" />
              </button>
            )}
            <img src="/logo.jpg" alt="Place des Énergies" className="h-10" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="text-gray-400 text-sm">Rapport d'Éligibilité TICGN{readOnly ? ' — Lecture seule' : ''}</span>
          </div>
          <div className="flex gap-2">
            {readOnly ? (
              <>
                <button onClick={() => window.print()} className="p-2 hover:bg-[#F5F6F9] rounded-lg" title="Imprimer" data-testid="print-btn">
                  <FileText className="w-5 h-5 text-[#687175]" />
                </button>
                <button
                  onClick={() => setPdfPreviewOpen(true)}
                  className="flex items-center gap-2 bg-[#F97316] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#EA580C]"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4" /> PDF
                </button>
              </>
            ) : (
              <>
                <ShareAccessBadge rapportType="ticgn" rapportId={id} />
                <button onClick={() => setShareOpen(true)} className="p-2 hover:bg-[#F5F6F9] rounded-lg" title="Partager (lecture seule)" data-testid="share-btn">
                  <Share2 className="w-5 h-5 text-[#687175]" />
                </button>
                <ValideClientBadge
                  valide={rapport.valide_client}
                  canToggle={isAdmin}
                  endpoint={`/ticgn/rapports/${rapport.id}/toggle-valide-client`}
                  onUpdated={(v) => setRapport({ ...rapport, valide_client: v })}
                />
                {isAdmin && (
                  <button onClick={handleLock} className="p-2 hover:bg-[#F5F6F9] rounded-lg" title={rapport.locked ? 'Déverrouiller' : 'Verrouiller'} data-testid="lock-btn">
                    {rapport.locked ? <Lock className="w-5 h-5 text-[#F97316]" /> : <Unlock className="w-5 h-5 text-[#687175]" />}
                  </button>
                )}
                <button
                  onClick={() => setPdfPreviewOpen(true)}
                  className="flex items-center gap-2 bg-[#F97316] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#EA580C]"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4" /> PDF
                </button>
                {isAdmin && (
                  <button onClick={handleDelete} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid="delete-btn"><Trash2 className="w-5 h-5" /></button>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {!readOnly && id && (
        <ShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          rapportType="ticgn"
          rapportId={id}
          rapportLabel={rapport?.nom_entreprise || rapport?.client?.nom || 'Rapport TICGN'}
        />
      )}

      <PdfPreviewModal
        open={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        url={pdfUrl}
        title={`Rapport TICGN — ${rapport?.nom_entreprise || rapport?.siren || ''}`}
        filename={pdfFilename}
      />

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Titre */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
            <Flame className="w-8 h-8 text-[#F97316]" />
            {rapport.nom_entreprise?.toUpperCase() || 'ENTREPRISE'}
          </h1>
          <div className="flex flex-wrap gap-4 mt-2 text-gray-500 text-sm">
            <span>SIREN : <span className="font-mono">{rapport.siren || '-'}</span></span>
            <span className="text-gray-300">|</span>
            <span>Période : <span className="font-medium">{rapport.debut_periode ? new Date(rapport.debut_periode).toLocaleDateString('fr-FR') : '-'} → {rapport.fin_periode ? new Date(rapport.fin_periode).toLocaleDateString('fr-FR') : '-'}</span></span>
            <span className="text-gray-300">|</span>
            <span>Ratio TICGN/VAC : <span className="font-semibold text-gray-700">{formatPercent(rapport.ratio_global)}</span></span>
            <span className="text-gray-300">|</span>
            <span className={`font-semibold ${isEligible ? 'text-emerald-600' : 'text-red-500'}`} data-testid="rapport-eligibilite-globale">
              {rapport.eligibilite_globale}
            </span>
          </div>
        </div>

        {/* Onglets */}
        <div className="bg-[#EEF2F6] rounded-lg p-1 mb-6 flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-md transition-all ${
                activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB 1: Vue d'ensemble */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Carte info TICGN */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start gap-3 mb-4">
                <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                <h3 className="font-semibold text-gray-900">Qu'est-ce que la TICGN ?</h3>
              </div>
              <div className="text-gray-600 text-sm leading-relaxed space-y-3 ml-8">
                <p>
                  La <strong>TICGN</strong> (Taxe Intérieure de Consommation sur le Gaz Naturel) est due par tous
                  les consommateurs de gaz au prorata de leur consommation, au tarif CERFA officiel de
                  <strong> 8,37 €/MWh</strong>.
                </p>
                <p>
                  Plusieurs voies permettent de bénéficier d'un <strong>tarif réduit</strong> ou d'une <strong>exonération</strong> :
                  ratio TICGN/VAC ≥ 0,5 %, ratio Valeur de Production &gt; 3 %, soumission aux quotas CO2,
                  rubrique ICPE qualifiante, double usage, etc.
                </p>
                <div className="mt-4 p-3 bg-orange-50 rounded-lg flex items-start gap-2">
                  <span>💡</span>
                  <p className="text-orange-800">
                    <strong>Bon à savoir :</strong> la fenêtre de remboursement TICGN s'étend sur les 2 années
                    précédentes + l'année en cours.
                  </p>
                </div>
              </div>
            </div>

            {/* Résultat éligibilité */}
            <div className={`rounded-xl border-2 p-6 ${isEligible ? 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white' : 'border-red-200 bg-gradient-to-br from-red-50 to-white'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Résultat d'éligibilité TICGN</p>
                  <p className={`text-4xl font-bold ${isEligible ? 'text-emerald-600' : 'text-red-500'}`}>
                    {rapport.eligibilite_globale}
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    {isEligible
                      ? `Voie(s) validante(s) : ${voiesValidantes.map(v => v.label).join(', ') || '—'}`
                      : `Ratio CERFA : ${formatPercent(rapport.ratio_global)} (seuil 0,500 %)`}
                  </p>
                </div>
                <div className={`p-4 rounded-full ${isEligible ? 'bg-emerald-100' : 'bg-red-100'}`}>
                  {isEligible ? <CheckCircle2 className="w-12 h-12 text-emerald-500" /> : <XCircle className="w-12 h-12 text-red-500" />}
                </div>
              </div>
              {isEligible && (
                <div className="mt-5 grid grid-cols-3 gap-4">
                  <div className="bg-white/70 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-500">Éco. Attestation</div>
                    <div className="text-xl font-bold text-emerald-600">{formatCurrency(economies_att)}</div>
                  </div>
                  <div className="bg-white/70 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-500">Éco. Remboursement</div>
                    <div className="text-xl font-bold text-blue-600">{formatCurrency(total_remb)}</div>
                  </div>
                  <div className="bg-white/70 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-500">Total</div>
                    <div className="text-xl font-bold text-[#F97316]">{formatCurrency(economies_att + total_remb)}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Encart pédagogique : comprendre l'éligibilité */}
            <EligibilityExplainer
              type="ticgn"
              eligible={isEligible}
              data={{
                ratio_global: rapport.ratio_global,
                valeur_ajoutee: rapport.valeur_ajoutee,
                total_conso_mwh: totalConsoMWh,
                ticgn_cerfa: totalTicgnCerfaOfficiel,
              }}
              accentColor="#F97316"
            />

            {/* Détails du calcul */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-gray-400" /> Détails du calcul d'éligibilité
              </h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Valeur Ajoutée Comptable (VAC)</span>
                    <span className="font-mono font-semibold text-gray-900" data-testid="rapport-vac">{formatCurrency(rapport.valeur_ajoutee)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Consommation totale</span>
                    <span className="font-mono font-semibold text-gray-900" data-testid="rapport-conso">{formatNumber(totalConsoMWh)} MWh</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">TICGN CERFA officiel<sup className="text-[#FF6B35] font-bold ml-0.5">*</sup> (8,37 €/MWh)</span>
                    <span className="font-mono font-semibold text-[#FF6B35]" data-testid="rapport-ticgn-cerfa">{formatCurrency(totalTicgnCerfaOfficiel)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">TICGN réel<sup className="text-[#FF6B35] font-bold ml-0.5">**</sup> (tarifs annuels)</span>
                    <span className="font-mono font-semibold text-gray-700" data-testid="rapport-ticgn-reel">{formatCurrency(totalTicgnReel)}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className={`flex justify-between items-center py-2 border-b-2 ${ratioCerfaOk ? 'border-emerald-200' : 'border-red-200'}`}>
                    <span className="text-sm font-medium">Ratio Énergies CERFA / VAC{(rapport.details_par_energie && Object.keys(rapport.details_par_energie).length > 1) ? ' (multi-énergies)' : ''}</span>
                    <span className={`font-mono font-bold text-lg ${ratioCerfaOk ? 'text-emerald-600' : 'text-red-500'}`} data-testid="rapport-ratio">
                      {formatPercent(rapport.ratio_global)}
                    </span>
                  </div>
                  {/* Détail multi-énergies (affiché uniquement si plus d'une énergie déclarée) */}
                  {rapport.details_par_energie && Object.keys(rapport.details_par_energie).length > 1 && (
                    <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-3 space-y-1.5" data-testid="rapport-multi-energie-detail">
                      <div className="text-xs font-semibold text-purple-900 mb-1">Détail par énergie (contribution au ratio)</div>
                      {Object.entries(rapport.details_par_energie).map(([code, info]) => (
                        <div key={code} className="flex justify-between text-xs">
                          <span className="text-purple-800">
                            {info.libelle || code}
                            <span className="text-purple-500 ml-1">({info.conso_mwh} MWh × {info.tarif} {info.unite || '€/MWh'})</span>
                          </span>
                          <span className="font-mono font-medium text-purple-900">{formatCurrency(info.cerfa)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-xs pt-1.5 border-t border-purple-200">
                        <span className="font-semibold text-purple-900">Total CERFA multi-énergies</span>
                        <span className="font-mono font-bold text-purple-900">{formatCurrency(rapport.total_cerfa_multi_energies || 0)}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Seuil d'éligibilité ratio CERFA</span>
                    <span className="font-mono text-gray-500">0,500 %</span>
                  </div>
                  {isEligible && (
                    <>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm text-gray-500">Économies attestation (12 mois)</span>
                        <span className="font-mono font-semibold text-emerald-600">{formatCurrency(economies_att)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 bg-emerald-50 rounded-lg px-3">
                        <span className="text-sm font-medium text-emerald-800">Total économies potentielles</span>
                        <span className="font-mono font-bold text-emerald-700 text-lg">{formatCurrency(economies_att + total_remb)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Légende explicative CERFA vs Réel */}
              <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500 leading-relaxed space-y-1.5" data-testid="cerfa-reel-legend">
                <p>
                  <sup className="text-[#FF6B35] font-bold">*</sup> <span className="font-medium text-gray-700">TICGN CERFA officiel</span> : taxe calculée au tarif administratif unique (8,37 €/MWh) retenu pour le ratio d'éligibilité et le remboursement via le formulaire CERFA. C'est la valeur de référence pour la démarche de récupération.
                </p>
                <p>
                  <sup className="text-[#FF6B35] font-bold">**</sup> <span className="font-medium text-gray-700">TICGN réel</span> : taxe effectivement payée à votre fournisseur, calculée sur les tarifs réglementés annuels. La différence entre les deux représente l'avantage tarifaire réel à activer.
                </p>
              </div>
            </div>
            {recommandation && (
              <div className="bg-blue-50 rounded-xl border-2 border-blue-200 p-6" data-testid="ticgn-reco-block">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 rounded-full p-3"><Calendar className="w-6 h-6 text-blue-600" /></div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">📅 Recommandations : Optimisation des périodes d'analyse TICGN</h3>
                    <div className="space-y-3 text-sm text-gray-700">
                      <p>
                        <span className="font-medium">Période analysée :</span>{' '}
                        {rapport.debut_periode ? new Date(rapport.debut_periode).toLocaleDateString('fr-FR') : '-'} au {rapport.fin_periode ? new Date(rapport.fin_periode).toLocaleDateString('fr-FR') : '-'}
                        {recommandation.moisAnalyses > 0 && <span className="text-gray-500"> ({recommandation.moisAnalyses} mois)</span>}
                      </p>
                      <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-4 mt-3">
                        <p className="font-semibold text-emerald-800 mb-2">💰 Base de calcul</p>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Économies période analysée :</p>
                            <p className="font-bold text-gray-900">{formatCurrency(recommandation.economiesAnalysees)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Remboursement mensuel moyen :</p>
                            <p className="font-bold text-gray-900">{formatCurrency(recommandation.economiesMensuellesRemboursement)}/mois</p>
                          </div>
                        </div>
                      </div>
                      {recommandation.hasPeriodeFuture && recommandation.moisManquants > 0 && (
                        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="ticgn-reco-future">
                          <p className="font-semibold text-amber-800 mb-2">⏩ Période future non couverte</p>
                          <p className="text-gray-700 mb-2">
                            Du {recommandation.debutPeriodeManquante.toLocaleDateString('fr-FR')} au {recommandation.finPeriodeManquante.toLocaleDateString('fr-FR')} ({recommandation.moisManquants} mois)
                          </p>
                          <p>💡 Économies estimées : <strong className="text-emerald-600">≈ {formatCurrency(recommandation.economiesEstimees)}</strong></p>
                        </div>
                      )}
                      {recommandation.hasPeriodePrecedente && (
                        <div className="bg-purple-50 border border-purple-300 rounded-lg p-4" data-testid="ticgn-reco-precedente">
                          <p className="font-semibold text-purple-800 mb-2">⏪ Période fiscale précédente (récupération TICGN)</p>
                          <p className="text-gray-700 mb-2">
                            Du {recommandation.debutPeriodePrecedente.toLocaleDateString('fr-FR')} au {recommandation.finPeriodePrecedente.toLocaleDateString('fr-FR')} ({recommandation.moisPeriodePrecedente} mois)
                          </p>
                          <p>💡 Économies estimées : <strong className="text-emerald-600">≈ {formatCurrency(recommandation.economiesEstimeesPrecedente)}</strong></p>
                          <p className="text-xs text-gray-500 mt-1">* Fenêtre TICGN : 2 années précédentes + année en cours.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Commentaires de l'analyste — Vue d'ensemble */}
            {(() => {
              const commentaireGlobal = (rapport.commentaire_global || '').trim();
              const commentairesSites = (rapport.sites || [])
                .map((s) => ({ nom: s.nom_site || s.siret, comment: (s.commentaire || '').trim() }))
                .filter((s) => s.comment);
              if (!commentaireGlobal && commentairesSites.length === 0) return null;
              return (
                <div className="bg-white rounded-xl border border-gray-200 p-8" data-testid="section-commentaires">
                  <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#F97316]" /> Commentaires de l'analyste
                  </h2>
                  {commentaireGlobal && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Commentaire général</p>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{commentaireGlobal}</p>
                    </div>
                  )}
                  {commentairesSites.length > 0 && (
                    <div className="space-y-3 mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Commentaires par site</p>
                      {commentairesSites.map((s, idx) => (
                        <div key={idx} className="bg-[#FFF7ED] border border-[#FED7AA] rounded-lg p-3">
                          <p className="text-xs font-semibold text-[#F97316] mb-1">{s.nom}</p>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{s.comment}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* TAB 2: Analyse financière */}
        {activeTab === 'analysis' && (
          <div className="space-y-6">
            {isEligible ? (
              <>
                {/* ============================================ */}
                {/* SECTION 1 — ÉCONOMIES CERTAINES (mise en avant) */}
                {/* ============================================ */}
                {total_remb > 0 && (
                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/70 rounded-xl border-2 border-emerald-400 p-6 shadow-sm" data-testid="section-economies-certaines">
                    <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-lg shrink-0">1</div>
                        <div>
                          <h3 className="font-bold text-emerald-900 text-lg flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-emerald-600" /> Économies validées — Période analysée
                          </h3>
                          <p className="text-sm text-emerald-800/70 mt-0.5">
                            Remboursement CERFA chiffré sur les <strong>données réelles fournies</strong>{libellePeriodeRemboursement && libellePeriodeRemboursement !== '—' ? ` (${libellePeriodeRemboursement})` : ''}.
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-emerald-700 uppercase tracking-wide">Total</p>
                        <p className="text-3xl font-extrabold text-emerald-700 font-mono" data-testid="total-remb-reel">{formatCurrency(total_remb)}</p>
                      </div>
                    </div>
                    {realYears.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                        {realYears.map(({ year, montant }) => (
                          <div key={year} className="bg-white rounded-lg p-4 text-center border-2 border-emerald-200 shadow-sm" data-testid={`card-real-${year}`}>
                            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">Année {year}</p>
                            <p className="text-xl font-bold text-emerald-600 font-mono">{formatCurrency(montant)}</p>
                            <p className="text-[10px] text-gray-500 mt-1">CERFA chiffré</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ============================================ */}
                {/* SECTION 2 — REMBOURSEMENT À CONFIRMER (estimations) */}
                {/* ============================================ */}
                {total_remb_estime > 0 && (
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100/60 rounded-xl border-2 border-purple-300 border-dashed p-6" data-testid="section-economies-estimees">
                    <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-lg shrink-0">2</div>
                        <div>
                          <h3 className="font-bold text-purple-900 text-lg flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-purple-600" /> Remboursement à confirmer — Périodes connexes
                          </h3>
                          <p className="text-sm text-purple-800/70 mt-0.5">
                            <strong>Estimations</strong> basées sur la moyenne des années renseignées. Éligibilité à confirmer avant démarche CERFA (tarif, NAF, VAC).
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-purple-700 uppercase tracking-wide">Sous-total estimé</p>
                        <p className="text-3xl font-extrabold text-purple-700 font-mono" data-testid="total-remb-estime">≈ {formatCurrency(total_remb_estime)}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                      {economiesParAnneeAvecEstimations.filter(e => e.estimation).map(({ annee, montant }) => (
                        <div key={annee} className="bg-white border-2 border-dashed border-purple-300 rounded-lg p-4 text-center" data-testid={`card-estime-${annee}`}>
                          <div className="flex items-center justify-center gap-1.5 mb-1">
                            <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Année {annee}</p>
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px] font-bold uppercase">Estimé</span>
                          </div>
                          <p className="text-xl font-bold text-purple-700 font-mono">≈ {formatCurrency(montant)}</p>
                          <p className="text-[10px] text-gray-500 mt-1">À confirmer</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ============================================ */}
                {/* SECTION 3 — ATTESTATION (économies futures)   */}
                {/* ============================================ */}
                {economies_att > 0 && (
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100/60 rounded-xl border-2 border-blue-300 p-6" data-testid="section-economies-attestation">
                    <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-lg shrink-0">3</div>
                        <div>
                          <h3 className="font-bold text-blue-900 text-lg flex items-center gap-2">
                            <FileText className="w-5 h-5 text-blue-600" /> Attestation — Économies potentielles futures
                          </h3>
                          <p
                            className="text-sm text-blue-800/70 mt-0.5"
                            title="L'attestation TICGN (CERFA 14705/14729) prend effet à compter de son dépôt et couvre 12 mois glissants."
                          >
                            Effet juridique à compter du dépôt, sur <strong>12 mois glissants</strong>.
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-blue-700 uppercase tracking-wide">Total</p>
                        <p className="text-3xl font-extrabold text-blue-700 font-mono" data-testid="total-attestation">{formatCurrency(economies_att)}</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border-2 border-blue-200 text-center">
                      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">
                        Période couverte
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-testid="periode-attestation">{libellePeriodeAttestation}</p>
                      <p className="text-[11px] text-gray-500 mt-1">À renouveler chaque année tant que l'éligibilité est maintenue</p>
                    </div>
                    <div className="mt-3 bg-blue-100/60 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 leading-relaxed" data-testid="note-eligibilite-attestation">
                      <strong>⚠️ Confirmation d'éligibilité requise</strong> — L'éligibilité au motif appliqué sur l'attestation
                      (ratio TICGN/VAC, Valeur de Production, ICPE, double usage, quotas CO2…) devra être confirmée
                      <strong> au terme de l'exercice fiscal couvert</strong>, dans un délai de <strong>6 mois</strong>.
                      Toute évolution de l'activité (changement de NAF, de tarif, ou perte de la voie d'éligibilité) doit
                      nous être signalée pour ajuster ou cesser l'attestation en cours.
                    </div>
                  </div>
                )}

                {/* ============================================ */}
                {/* SECTION 4 — GRAPHIQUE GLOBAL                  */}
                {/* ============================================ */}
                {economiesParAnneeAvecEstimations.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="section-graphique-global">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-white font-bold text-lg shrink-0">4</div>
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-gray-700" /> Vue d'ensemble — Toutes les économies
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">Visualisation cumulée du remboursement CERFA (réel + estimé) et de l'attestation par période.</p>
                      </div>
                    </div>

                    {/* Légende synthétique */}
                    <div className="flex flex-wrap gap-3 mb-4 text-xs">
                      <div className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-emerald-500" /> Remboursement réel</div>
                      <div className="flex items-center gap-1.5"><span className="w-4 h-4 rounded" style={{ background: 'repeating-linear-gradient(45deg, #DDD6FE, #DDD6FE 3px, #8B5CF6 3px, #8B5CF6 5px)' }} /> Remboursement estimé</div>
                      <div className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-blue-500" /> Attestation (futur)</div>
                    </div>

                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={graphiqueGlobal}
                          margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
                          barCategoryGap="25%"
                        >
                          <defs>
                            <pattern id="hatchPattern-ticgn" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                              <rect width="6" height="6" fill="#DDD6FE" />
                              <line x1="0" y1="0" x2="0" y2="6" stroke="#8B5CF6" strokeWidth="2" />
                            </pattern>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="label" />
                          <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} />
                          <Tooltip
                            formatter={(v, name, props) => [
                              `${props.payload?.type === 'estime' ? '≈ ' : ''}${formatCurrency(v)}`,
                              props.payload?.typeLabel || 'Économie'
                            ]}
                          />
                          <Bar
                            dataKey="montant"
                            name="Économie"
                            maxBarSize={90}
                            radius={[4, 4, 0, 0]}
                            shape={(props) => {
                              const { x, y, width, height, payload } = props;
                              let fill = '#10B981'; // réel
                              if (payload?.type === 'estime') fill = 'url(#hatchPattern-ticgn)';
                              else if (payload?.type === 'attestation') fill = '#3B82F6';
                              return <rect x={x} y={y} width={width} height={height} fill={fill} rx={4} />;
                            }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <p className="text-xs text-gray-600 mt-3 px-2 leading-relaxed bg-gray-50 border border-gray-200 rounded p-3" data-testid="chart-info-note">
                      <strong>Lecture :</strong> chaque barre représente une période. Le remboursement CERFA est rétroactif sur les années passées ;
                      l'attestation s'applique sur les 12 mois suivant son dépôt. Les barres hachurées violettes sont des estimations basées sur la moyenne
                      des années renseignées et doivent être confirmées avant toute démarche CERFA.
                    </p>
                  </div>
                )}

                {/* ============================================ */}
                {/* SECTION 5 — TOTAL POTENTIEL                   */}
                {/* ============================================ */}
                <div className="bg-gradient-to-r from-[#F97316] to-[#EA580C] rounded-xl p-6 text-white" data-testid="kpi-total-economies">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-orange-100 text-sm mb-1 uppercase tracking-wider">Économie totale potentielle</p>
                      <p className="text-5xl font-extrabold font-mono" data-testid="total-potentiel">+{formatCurrency(total_potentiel)}</p>
                    </div>
                    <TrendingDown className="w-16 h-16 text-orange-200" />
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 space-y-1.5 text-sm">
                    {total_remb > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-orange-50">
                          <span className="inline-block w-2 h-2 rounded-full bg-emerald-300 mr-1.5" />
                          <strong>① Remboursement réel</strong> · {libellePeriodeRemboursement} <span className="text-orange-200 text-xs">(CERFA chiffré)</span>
                        </span>
                        <span className="font-mono font-semibold">{formatCurrency(total_remb)}</span>
                      </div>
                    )}
                    {total_remb_estime > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-orange-50">
                          <span className="inline-block w-2 h-2 rounded-full bg-purple-300 mr-1.5" />
                          <strong>② Remboursement estimé</strong> · {economiesParAnneeAvecEstimations.filter(e => e.estimation).map(e => e.annee).join(', ')} <span className="text-orange-200 text-xs">(à confirmer)</span>
                        </span>
                        <span className="font-mono font-semibold">≈ {formatCurrency(total_remb_estime)}</span>
                      </div>
                    )}
                    {economies_att > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-orange-50">
                          <span className="inline-block w-2 h-2 rounded-full bg-blue-300 mr-1.5" />
                          <strong>③ Attestation</strong> · {libellePeriodeAttestation}
                        </span>
                        <span className="font-mono font-semibold">{formatCurrency(economies_att)}</span>
                      </div>
                    )}
                    <div className="border-t border-white/30 pt-1.5 flex items-center justify-between font-bold">
                      <span>Total potentiel</span>
                      <span className="font-mono">{formatCurrency(total_potentiel)}</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-amber-50 rounded-xl border-2 border-amber-200 p-8">
                <div className="flex items-center gap-4">
                  <XCircle className="w-12 h-12 text-amber-500" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Non éligible aux taux réduits TICGN</h3>
                    <p className="text-gray-600">
                      Aucune voie d'éligibilité (ratio CERFA, VP, ICPE, double usage, quotas CO2) n'est ouverte avec
                      les données fournies. Consultez l'onglet « Éligibilité » pour le détail des voies analysées.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: Détails PCE */}
        {activeTab === 'details' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-1">Points de Comptage et d'Estimation (PCE)</h3>
            <p className="text-gray-500 text-sm mb-6">Détail de la consommation, du calcul et des tranches d'éligibilité par PCE</p>
            <div className="space-y-4">
              {pces.map((pce, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4" data-testid={`details-pce-${pce.pce}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-mono font-semibold text-gray-900">{pce.pce}</span>
                      <span className="text-gray-500 text-sm ml-3">NAF : {pce.code_naf}</span>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${pce.eligibilite === 'ELIGIBLE' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {pce.eligibilite}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-3 text-sm mb-3">
                    <div><span className="text-gray-500">Conso :</span> <strong>{(pce.total_conso_mwh || 0).toFixed(1)} MWh</strong></div>
                    <div><span className="text-gray-500">TICGN CERFA :</span> <strong>{formatCurrency(pce.ticgn_cerfa_officiel || 0)}</strong></div>
                    <div><span className="text-gray-500">Ratio :</span> <strong>{((pce.ratio || 0) * 100).toFixed(3)} %</strong></div>
                    <div><span className="text-gray-500">Éco. Att. :</span> <strong className="text-emerald-600">{formatCurrency(pce.economies_attestation || 0)}</strong></div>
                  </div>
                  {pce.icpe_rubrique?.numero && pce.icpe_motif && (
                    <div className="mb-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800" data-testid={`pce-icpe-${pce.pce}`}>
                      <strong>Rubrique ICPE invoquée :</strong> {pce.icpe_rubrique.numero}
                      {pce.icpe_rubrique.libelle && ` — ${pce.icpe_rubrique.libelle}`}
                    </div>
                  )}
                  {pce.tranches_detail?.length > 0 && (() => {
                    // Sur le rapport définitif : si le PCE est éligible, on n'affiche
                    // que les tranches éligibles (motif validant). Sinon, on affiche
                    // toutes les tranches non-NORMAL renseignées par l'utilisateur (s'il
                    // n'y en a pas, on ne montre pas le bloc).
                    const tranchesAffichees = pce.eligibilite === 'ELIGIBLE'
                      ? pce.tranches_detail.filter(t => t.eligible && t.motif !== 'NORMAL')
                      : pce.tranches_detail.filter(t => t.motif !== 'NORMAL');
                    if (tranchesAffichees.length === 0) return null;
                    return (
                      <div className="border-t border-gray-100 pt-3">
                        <div className="text-xs font-semibold text-gray-500 mb-2">
                          {pce.eligibilite === 'ELIGIBLE' ? 'Motif(s) validant(s)' : 'Motif(s) renseigné(s)'}
                        </div>
                        <div className="space-y-2">
                          {tranchesAffichees.map((t, ti) => {
                            const isExo = (t.motif || '').startsWith('EXO') || t.motif === 'EXONERATION';
                            const colorClass = t.eligible
                              ? (isExo ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700')
                              : 'bg-amber-100 text-amber-700';
                            return (
                              <div key={ti} className="space-y-1">
                                <div className="flex items-center gap-3 text-sm flex-wrap">
                                  <span className={`inline-block text-xs px-1.5 py-0.5 rounded font-medium ${colorClass}`}>
                                    {t.eligible ? '✓' : '✗'} {t.part}% — {MOTIF_LABELS[t.motif] || t.motif}
                                  </span>
                                  {t.soumis_ratio && <span className="text-[10px] text-gray-500">(ratio TICGN/VAC ≥ 0,5 %)</span>}
                                  <span className="text-gray-500">Taux : {t.taux} €/MWh</span>
                                  <span className="text-gray-500">TICGN : {formatCurrency(t.ticgn_cerfa || 0)}</span>
                                  {t.eligible && (t.economies_attestation || 0) > 0 && (
                                    <span className="text-emerald-600 font-semibold">Éco : {formatCurrency(t.economies_attestation)}</span>
                                  )}
                                </div>
                                {(t.cas_code || t.justificatif) && (
                                  <div className="pl-2 text-xs text-gray-500 italic" data-testid={`tranche-justif-${pce.pce}-${ti}`}>
                                    {t.cas_code && (
                                      <span className="not-italic mr-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded font-mono text-[10px]">
                                        {t.cas_code}
                                      </span>
                                    )}
                                    {t.justificatif && <>Justificatif : {t.justificatif}</>}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>

            {/* Graphique consommation mensuelle */}
            {consoChartData.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-3 text-sm">Consommation mensuelle (MWh)</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={consoChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="mois" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => [`${v.toFixed(1)} MWh`, 'Consommation']} />
                    <Bar dataKey="conso" fill="#F97316" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* TAB 4: Éligibilité */}
        {activeTab === 'eligibility' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-1">
                {isEligible ? 'Voie(s) d\'éligibilité validée(s)' : 'Voies d\'éligibilité analysées'}
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                {isEligible
                  ? 'Le rapport est éligible via les voies suivantes. Seules les voies qui ouvrent l\'éligibilité sont affichées.'
                  : 'Aucune voie n\'est ouverte. Détail des points sur lesquels le rapport a été édité — les critères non renseignés ne sont pas listés.'}
              </p>

              {voiesAffichees.length === 0 ? (
                <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-500 italic" data-testid="voies-empty">
                  Aucune voie d'éligibilité renseignée n'est applicable. Consultez l'onglet « Vue d'ensemble » pour le détail
                  des données de calcul.
                </div>
              ) : (
                <div className="space-y-3">
                  {voiesAffichees.map(v => (
                    <div
                      key={v.key}
                      data-testid={`rapport-voie-${v.key}`}
                      className={`p-4 rounded-lg border ${v.statut === 'eligible' ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50/30'}`}
                    >
                      <div className="flex items-start gap-3">
                        {v.statut === 'eligible'
                          ? <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                          : <XCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />}
                        <div className="flex-1">
                          <div className="flex items-baseline gap-2">
                            <span className="text-xs font-mono text-gray-500">{v.label}.</span>
                            <p className="font-medium text-gray-900">{v.titre}</p>
                          </div>
                          <p className="text-sm text-gray-700 mt-1">{v.valeur}</p>
                          <p className="text-xs text-gray-500 italic mt-1">{v.detail}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Bloc VP / Quotas — uniquement si renseigné */}
            {(quotasRenseignee || vpRenseignee || vpSiretRenseignee) && (
              <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="rapport-vp-block">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 flex-wrap">
                  🧮 Vérifications complémentaires renseignées
                  {quotasRenseignee && <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs">Quotas CO2 ✓</span>}
                  {vpRenseignee && (
                    vpEligible
                      ? <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded text-xs">VP éligible (&gt;3 %) ✓</span>
                      : <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs">VP non éligible (&lt;3 %)</span>
                  )}
                </h3>
                {vpRenseignee && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><div className="text-xs text-gray-500">CA</div><div className="font-semibold">{formatCurrency(vpData.ca || 0)}</div></div>
                      <div><div className="text-xs text-gray-500">Export</div><div className="font-semibold">{formatCurrency(vpData.export || 0)}</div></div>
                      <div><div className="text-xs text-gray-500">Import</div><div className="font-semibold">{formatCurrency(vpData.import || 0)}</div></div>
                      <div><div className="text-xs text-gray-500">Valeur de Production</div><div className="font-semibold text-purple-700">{formatCurrency(vpData.valeur_production_calculee || 0)}</div></div>
                    </div>
                    {vpMethodeNominale ? (
                      <div className="mt-3" data-testid="vp-lignes-table">
                        <div className="text-xs font-semibold text-gray-700 mb-1.5">Détail des consommations énergétiques (méthode officielle)</div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-purple-100 text-purple-900">
                              <th className="text-left py-1 px-2">Catégorie fiscale</th>
                              <th className="text-right py-1 px-2">Conso (MWh)</th>
                              <th className="text-right py-1 px-2">Tarif (€/MWh)</th>
                              <th className="text-right py-1 px-2">Coût fiscal (€)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vpLignes.map((l, i) => (
                              <tr key={i} className="border-b border-purple-100">
                                <td className="py-1 px-2">{l.libelle || l.energie_code}</td>
                                <td className="py-1 px-2 text-right font-mono">{(l.consommation_mwh || 0).toLocaleString('fr-FR', { maximumFractionDigits: 3 })}</td>
                                <td className="py-1 px-2 text-right font-mono">{(l.tarif_fiscal || 0).toLocaleString('fr-FR', { maximumFractionDigits: 3 })}</td>
                                <td className="py-1 px-2 text-right font-semibold">{formatCurrency(l.sous_total || 0)}</td>
                              </tr>
                            ))}
                            <tr className="bg-purple-50 font-semibold">
                              <td className="py-1 px-2" colSpan={3}>Σ Coût fiscal énergétique</td>
                              <td className="py-1 px-2 text-right text-purple-700">{formatCurrency(vpCoutTotal)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-3">
                        <div><div className="text-xs text-gray-500">Factures Élec HTVA</div><div className="font-semibold">{formatCurrency(vpData.factures_elec || 0)}</div></div>
                        <div><div className="text-xs text-gray-500">Factures Gaz HTVA</div><div className="font-semibold">{formatCurrency(vpData.factures_gaz || 0)}</div></div>
                        <div><div className="text-xs text-gray-500">Factures autres énergies</div><div className="font-semibold">{formatCurrency(vpData.factures_autres || 0)}</div></div>
                        <div><div className="text-xs text-gray-500">Σ Factures HTVA (legacy)</div><div className="font-semibold">{formatCurrency(vpCoutTotal)}</div></div>
                      </div>
                    )}
                    <div className="mt-3 text-sm">
                      <span className="text-gray-500">Ratio VP (SIREN) : </span>
                      <strong className={vpEligible ? 'text-emerald-700' : 'text-amber-700'}>
                        {((vpData.ratio_vp || 0) * 100).toFixed(2).replace('.', ',')} %
                      </strong>
                      <span className="text-xs text-gray-500 ml-2">(seuil : 3 %)</span>
                      {!vpMethodeNominale && (
                        <span className="ml-2 text-[10px] text-gray-400 italic">Rapport ancien — méthode legacy factures HTVA</span>
                      )}
                    </div>
                  </>
                )}
                {vpSiretRenseignee && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <div className="text-xs font-semibold text-gray-700 mb-2">Variante VP par SIRET</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      {vpParSiretEntries.map(([siret, v]) => {
                        const cout = v.cout_total_fiscal ?? v.factures_total_htva ?? 0;
                        const hasLignes = (v.lignes_energies || []).length > 0;
                        return (
                          <div key={siret} className={`p-2 rounded border ${v.eligible_vp ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`}
                            data-testid={`vp-siret-display-${siret}`}>
                            <div className="font-mono">{siret}</div>
                            <div className="flex items-center justify-between">
                              <span>{hasLignes ? 'Σ Conso × Tarif' : 'Σ Factures'} {formatCurrency(cout)} / VP {formatCurrency(v.valeur_production_calculee || 0)}</span>
                              <strong className={v.eligible_vp ? 'text-emerald-700' : 'text-amber-700'}>
                                {((v.ratio_vp || 0) * 100).toFixed(2).replace('.', ',')} %
                              </strong>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Conclusion */}
            <div className={`rounded-xl border-2 p-8 ${isEligible ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-center gap-4">
                {isEligible ? <CheckCircle2 className="w-12 h-12 text-emerald-500" /> : <XCircle className="w-12 h-12 text-red-500" />}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {isEligible ? 'Éligible aux réductions / exonérations TICGN' : 'Non éligible aux réductions TICGN'}
                  </h3>
                  <p className="text-gray-600">
                    {isEligible
                      ? `Voie(s) ouverte(s) : ${voiesValidantes.map(v => v.label + ' — ' + v.titre).join(' • ')}.`
                      : `Aucune des voies analysées n'atteint les seuils requis avec les données fournies (ratio CERFA ${formatPercent(rapport.ratio_global)}).`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: Informations (Suite à donner + Responsabilité) */}
        {activeTab === 'informations' && (
          <div className="space-y-6" data-testid="tab-informations">
            <div className="bg-white rounded-xl border border-gray-200 p-8" data-testid="section-suite-a-donner">
              <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-emerald-500" /> Suite à donner
              </h2>
              <div className="text-sm text-gray-600 leading-relaxed space-y-3">
                <p>En cas d'accord du client pour la mise en place de l'optimisation TICGN, les étapes suivantes seront engagées :</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Collecte et vérification des pièces justificatives (liasse fiscale, attestation sur l'honneur, factures de gaz, justificatifs ICPE / double usage / quotas CO2 le cas échéant)</li>
                  <li>Préparation et envoi de l'attestation d'éligibilité au fournisseur de gaz pour application du tarif réduit / exonération sur les 12 prochains mois</li>
                  <li>Constitution et dépôt du dossier de remboursement TICGN auprès de l'administration fiscale (fenêtre : 2 années précédentes + année en cours)</li>
                  <li>Suivi du traitement du dossier et accompagnement jusqu'à l'obtention effective des économies</li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-8" data-testid="section-responsabilite">
              <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-500" /> Responsabilité
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Les estimations d'économies présentées sont établies sur la base des informations transmises par le client
                (factures, données de consommation, valeur ajoutée comptable, code NAF, valeur de production, soumission aux
                quotas CO2, rubrique ICPE, double usage, etc.). Place des Énergies ne saurait être tenue responsable en cas
                d'inexactitude ou d'incomplétude des données fournies. Les montants indiqués sont des estimations et peuvent
                varier en fonction des conditions réelles d'application par l'administration fiscale et le fournisseur de gaz.
              </p>
            </div>

            {!readOnly && id && (
              <RapportAuditHistory rapportType="ticgn" rapportId={id} />
            )}
          </div>
        )}

      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-12">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between text-sm text-gray-400">
          <span>Place des Énergies — Rapport TICGN généré le {new Date().toLocaleDateString('fr-FR')}</span>
          <span>SIREN {rapport.siren}</span>
        </div>
      </footer>
    </div>
  );
};

export default RapportTICGN;
